/* eslint-disable no-secrets/no-secrets */
import React, { useState, useContext, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import { wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { withRouter } from 'react-router-dom';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { Button } from '@jutro/legacy/components';
import { useTranslator } from '@jutro/locale';

import metadata from './RiskInformationComponent.metadata.json5';
import styles from '../../../pages/EquipmentBreakdown/EquipmentBreakdownPage.module.scss';
import messages from './RiskInformationComponent.messages.js';
import PCTClassCodeFilterTableComponent from '../PCTClassCodeFilterTable/PCTClassCodeFilterTableComponent';

function RiskInformationComponent(props) {
    const { submissionVM, updateWizardData, pathUpdated } = props;
    const { authHeader } = useAuthentication();
    const breakpoint = useContext(BreakpointTrackerContext);
    const { onValidate } = useValidation('RiskInformationComponent');
    const modelVM = _.get(submissionVM, pathUpdated);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const translator = useTranslator();
    const [industryCodeSearchVisible, setIndustryCodeSearchVisible] = useState(false);
    const [ibcCode, updateIbcCode] = useState('');
    const [industryDescription, updateIndustryDescription] = useState('');
    const [industryCategory, updateIndustryCategory] = useState('');
    const indCatFiePath = 'lobData.consTraAndServLine.coverables.equipmentBreakdown.riskInformation.indCatFie.value';
    const indDesFiePath = 'lobData.consTraAndServLine.coverables.equipmentBreakdown.riskInformation.indDesFie.value';
    const indDesStringFiePath = 'lobData.consTraAndServLine.coverables.equipmentBreakdown.riskInformation.indDesString.value';

    modelVM.value.isValidationReq = true;

    const onCell = (item, index, { id: property }) => {
        return item[property];
    };

    const renderSelectButton = (item, index, { id: property }) => {
        return (
            <Button type='text'  onClick={(e) => onActionClick(item)}>
                Select
            </Button>
        );
    };

    const loadData = ({ pageSize, page }) => {
        const paginationParams = {
            fromPage: page,
            pageSize: pageSize
        };

        return searchIndustryCode(paginationParams);
    };

    const searchIndustryCode = async(paginationParams) => {
        const pctEquBreClassCodeDTO = {
            iBCCode: ibcCode,
            industryCategory: industryCategory,
            descriptionOfOccupancy: industryDescription
        };

        if(!paginationParams) {
            paginationParams = {
                fromPage: 0,
                pageSize: 10
            };
        }

        const reqParam = {
            pctEquBreClassCodeDTO: pctEquBreClassCodeDTO,
            pageNumber: paginationParams.fromPage,
            pageSize: paginationParams.pageSize
        }
        
        const response = await LoadSaveService.getPCTEquBreClassCodes_Ext(reqParam, authHeader);
        setIndustryCodeSearchVisible(true);
        if(response != null){
            return {
                rows: response.pctEquBreClassCodes,
                numberOfRows: response.maxNumberOfResults
            };

        } else {
            return {
                rows: [],
                numberOfRows: 0
            };
        }
    };

    const clearSearchTexts = () => {
        updateIbcCode('');
        updateIndustryCategory('');
        updateIndustryDescription('');
    }

    const cancelSearch = () => {
        setIndustryCodeSearchVisible(false);
    }

    const openSearchPanel = () => {
        setIndustryCodeSearchVisible(true);
    } 

    const clearIndustryDescription = () => {
        _.set(submissionVM, indCatFiePath, '');
        updateWizardData(submissionVM);
    }


    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onActionClick = (object, index) => {
        _.set(submissionVM, indCatFiePath, object.industryCategory); 
        _.set(submissionVM, indDesFiePath, object.descriptionOfOccupancy);
        _.set(submissionVM, indDesStringFiePath, `${object.descriptionOfOccupancy}+${object.industryCategory}`);
        setIndustryCodeSearchVisible(false);
        updateWizardData(submissionVM);
    };


    // eslint-disable-next-line react-hooks/exhaustive-deps
    const overrideProps = {
        '@field': {
            showRequired: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },
        industryCodeSearchBtn: {
            onClick: () => { searchIndustryCode(); },
            visible: false
        },
        industryGroupAdapters: {
            visible: industryCodeSearchVisible,
            key: ibcCode,
            onFetchData: loadData
        },
        industryGroupSearchContainer: {
            visible: industryCodeSearchVisible
        },
        industryGroupAllContainer: {
            visible: industryCodeSearchVisible
        },
        industryGroupClearIconBtn: {
            onClick: () => { clearIndustryDescription();},
            title: translator(messages.clearCategory)
        },
        industryCodeResetBtn: {
            onClick: () => { clearSearchTexts(); }
        },
        industryCodeCancelBtn: {
            onClick: () => {cancelSearch(); }
        }, 
        industrySearchIconBtn: {
            onClick: () => {openSearchPanel(); },
            title: translator(messages.searchCategory)
        },
        industryCodeSearchInput: {
            value : ibcCode,
            onValueChange: updateIbcCode
        },
        industryCategorySearchInput : {
            value: industryCategory,
            onValueChange: updateIndustryCategory
        },
        industryDesSearchInput: {
            value: industryDescription,
            onValueChange: updateIndustryDescription
        },
        searchResultHeader: {
            visible: industryCodeSearchVisible
        }

    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate,
            onActionClick,
            onCell,
            renderSelectButton
        },
        resolveComponentMap: {
            pctClassCodePaginatedFilterTable: PCTClassCodeFilterTableComponent
        },
        resolveClassNameMap: styles
    };

    const readValue = useCallback(
        (id, path) => readViewModelValue(metadata.componentContent, _.get(submissionVM, pathUpdated), id, path, overrideProps),
        [overrideProps, pathUpdated, submissionVM]
    );

    const handleValueChange = useCallback(
        (value, changedPath) => {
            const fullPath = `${pathUpdated}.${changedPath}`;

            _.set(submissionVM, `${fullPath}`, value);

            updateWizardData(submissionVM);
        },
        [pathUpdated, submissionVM, updateWizardData]
    );

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            resolveValue={readValue}
            overrideProps={overrideProps}
            onValidationChange={onValidate}
            onValueChange={handleValueChange}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

RiskInformationComponent.propTypes = wizardProps;
export default withRouter(RiskInformationComponent);